import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Building2, Users, Shield, ArrowRight } from 'lucide-react'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  KpiCard,
  Spinner,
} from '@rr/ui'
import { isAuthenticated, getUser } from '@/lib/auth/session'
import type { PlatformUser } from '@/types'
import { NAV_GROUPS } from '@/lib/nav'
import { usePlatformStats } from '@/modules/platform/hooks/usePlatformStats'

export default function HomePage() {
  const navigate = useNavigate()
  const [user, setUser] = useState<PlatformUser | null>(null)
  const [loading, setLoading] = useState(true)
  const { data: stats, isLoading: statsLoading } = usePlatformStats()

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login')
      return
    }
    const userData = getUser()
    if (userData) setUser(userData as PlatformUser)
    setLoading(false)
  }, [navigate])

  const fmt = (v: number | string | undefined | null) => {
    if (statsLoading) return '…'
    if (v === undefined || v === null) return '—'
    return String(v)
  }
  const s = stats as any

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    )
  }
  if (!user) return null

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-h1 font-semibold mb-1">Platform dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back, {user.name}.
          </p>
        </header>

        <section
          aria-label="Platform metrics"
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
        >
          <KpiCard
            label="Organizations"
            value={fmt(s?.organizations?.total)}
            subtext={`${fmt(s?.organizations?.active)} active`}
            icon={Building2}
            iconBgClass="bg-blue-50"
            iconClass="text-info"
          />
          <KpiCard
            label="Tenant users"
            value={fmt(s?.tenantUsers?.total)}
            subtext="Across all organizations"
            icon={Users}
            iconBgClass="bg-emerald-50"
            iconClass="text-success"
          />
          <KpiCard
            label="Platform admins"
            value={fmt(s?.admins?.total)}
            subtext="System operators"
            icon={Shield}
            iconBgClass="bg-amber-50"
            iconClass="text-warning"
          />
        </section>

        <section aria-label="Jump into a section" className="mb-10">
          <h2 className="text-h3 font-semibold mb-4">Jump into a section</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAV_GROUPS.filter((g) => g.key !== 'tenants').map((g) => (
              <Card key={g.key}>
                <CardHeader>
                  <CardTitle className="text-h3">{g.label}</CardTitle>
                  <CardDescription>
                    {g.tabs.length} sub-section{g.tabs.length === 1 ? '' : 's'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground truncate">
                    {g.tabs.slice(0, 3).map((t) => t.label).join(' · ')}
                    {g.tabs.length > 3 ? ' · …' : ''}
                  </span>
                  <Button asChild variant="outline" size="sm">
                    <Link to={g.href}>
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Recent activity</CardTitle>
            <CardDescription>
              Activity feed will appear once the audit log is wired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground py-8 text-center">
              No recent activity.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
